import { Minus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { STEP_TITLES } from "./types";
import { ScanStatus } from "./ScanStatus";

interface WizardHeaderProps {
  step: number;
  displayStep?: number;
  total?: number;
  title?: string;
  onMinimize: () => void;
  onClose: () => void;
  showCloseConfirm: boolean;
  onConfirmClose: () => void;
  onCancelClose: () => void;
  /** Scan status shown persistently in the header (FE change 100). */
  scan?: { pct: number; message: string; isExtracting: boolean; isDone: boolean; celebrateLine?: string };
}

export function WizardHeader({
  step, displayStep, total = 7, title, onMinimize, onClose,
  showCloseConfirm, onConfirmClose, onCancelClose, scan,
}: WizardHeaderProps) {
  const shownStep = displayStep ?? step;
  return (
    <div className="flex items-center justify-between px-6 h-[52px] border-b border-border/50 bg-background shrink-0">
      <div className="flex items-center gap-3">
        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-blue-600/20 border border-blue-500/25 shrink-0">
          <span className="text-sm font-bold text-blue-400 leading-none select-none">R</span>
        </div>
        <div className="h-3.5 w-px bg-border/60" />
        <div>
          <p className="text-label font-medium text-subtle-foreground uppercase tracking-widest leading-none">
            Step {shownStep} of {total}
          </p>
          <h2 className="text-base font-semibold text-foreground leading-snug mt-0.5">
            {title ?? STEP_TITLES[shownStep] ?? "Wizard"}
          </h2>
        </div>
      </div>

      {scan && (
        <div className="flex-1 flex justify-center px-4 min-w-0">
          <ScanStatus
            pct={scan.pct}
            message={scan.message}
            isExtracting={scan.isExtracting}
            isDone={scan.isDone}
            celebrateLine={scan.celebrateLine}
          />
        </div>
      )}

      <div className="flex items-center gap-0.5">
        {showCloseConfirm ? (
          <div className="flex items-center gap-2 animate-in fade-in duration-150">
            <span className="text-md text-muted-foreground/70">Exit setup?</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="destructive" className="h-7 px-3 text-md" onClick={onConfirmClose}>
                  Exit
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-left">
                <p className="text-sm leading-relaxed">Close the wizard. Your progress so far is saved.</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" variant="outline" className="h-7 px-3 text-md border-border/60" onClick={onCancelClose}>
                  Stay
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-left">
                <p className="text-sm leading-relaxed">Keep working through the setup steps.</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "h-7 w-7 rounded-md transition-colors",
                    "text-subtle-foreground hover:text-muted-foreground hover:bg-muted/40"
                  )}
                  onClick={onMinimize}
                >
                  <Minus className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-left">
                <p className="text-sm leading-relaxed">Minimise the wizard and continue later.</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className={cn(
                    "h-7 w-7 rounded-md transition-colors",
                    "text-subtle-foreground hover:text-foreground hover:bg-muted/40"
                  )}
                  onClick={onClose}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-left">
                <p className="text-sm leading-relaxed">Close or exit the wizard. Progress is saved after each step.</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}
